import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Upload, ArrowLeft, Cloud, AlertCircle, Trash2 } from "lucide-react";
import * as tus from "tus-js-client";

export default function ResumeUpload() {
  const { id } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tusUpload, setTusUpload] = useState(null);

  useEffect(() => {
    api.get(`/movies/${id}/resume-info`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(e.response?.data?.detail || "Could not load upload info"));
  }, [id]);

  const startResume = () => {
    if (!file || !info) return;
    if (info.total_size_bytes && file.size !== info.total_size_bytes) {
      return toast.error(
        `File size mismatch: this upload reserved ${(info.total_size_bytes / 1024 / 1024).toFixed(1)} MB, but the file you picked is ${(file.size / 1024 / 1024).toFixed(1)} MB. Select the original file or cancel and start a new upload.`,
        { duration: 12000 }
      );
    }

    setSubmitting(true);
    setPhase("uploading");
    setProgress(info.percent || 0);

    const upload = new tus.Upload(file, {
      uploadUrl: info.upload_url,        // resume directly — tus does HEAD to learn current offset
      chunkSize: 50 * 1024 * 1024,
      retryDelays: [0, 1000, 3000, 5000, 10000],
      metadata: { filename: file.name, filetype: file.type },
      onError: (err) => {
        console.error("Resume tus error:", err);
        toast.error(`Resume failed: ${err?.message || "network error"}. Try again or cancel and restart.`, { duration: 10000 });
        setSubmitting(false);
        setPhase("");
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        setProgress(Math.round((bytesUploaded / bytesTotal) * 100));
      },
      onSuccess: async () => {
        setProgress(100);
        setPhase("processing");
        try {
          await api.post(`/movies/${id}/upload-started`);
        } catch { /* non-fatal */ }
        toast.success("Upload complete! Cloudflare is transcoding — you'll see it under 'Pending review' shortly.", { duration: 8000 });
        setTimeout(() => nav("/creator"), 1500);
      },
    });

    setTusUpload(upload);
    upload.start();
  };

  const cancelResume = () => {
    if (tusUpload) tusUpload.abort();
    setTusUpload(null);
    setSubmitting(false);
    setPhase("");
    setProgress(0);
  };

  const deleteMovie = async () => {
    if (!window.confirm("Cancel this upload and delete the draft? You'll need to start over from the upload page.")) return;
    try {
      await api.delete(`/movies/${id}`);
      toast.success("Upload cancelled");
      nav("/creator");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    }
  };

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="resume-error">
        <AlertCircle className="h-12 w-12 mx-auto text-[#E65F2B] mb-4" />
        <h2 className="font-display text-3xl font-bold">Can&apos;t resume this upload</h2>
        <p className="text-white/60 mt-3 max-w-md mx-auto">{error}</p>
        <Link to="/creator/upload" className="ember-btn mt-6 inline-flex">Start a new upload</Link>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-[#E65F2B] border-t-transparent animate-spin" />
      </div>
    );
  }

  const sizeMB = info.total_size_bytes ? (info.total_size_bytes / 1024 / 1024).toFixed(1) : null;
  const remainingMB = info.total_size_bytes && info.bytes_uploaded
    ? ((info.total_size_bytes - info.bytes_uploaded) / 1024 / 1024).toFixed(1)
    : null;

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-12 py-12" data-testid="resume-upload-page">
      <Link to="/creator" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-8" data-testid="back-to-creator">
        <ArrowLeft className="h-4 w-4" /> Back to Studio
      </Link>
      <p className="text-eyebrow text-[#EAB308] flex items-center gap-2 mb-3">
        <Cloud className="h-3.5 w-3.5" /> Resume incomplete upload
      </p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter" data-testid="resume-movie-title">{info.title}</h1>

      {info.percent !== null && info.percent !== undefined && (
        <div className="card-surface p-6 mt-8" data-testid="resume-status-card">
          <div className="flex justify-between text-sm">
            <span className="text-white/60">Already uploaded</span>
            <span className="text-[#E65F2B] font-bold tabular-nums" data-testid="resume-percent">{info.percent}%</span>
          </div>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden mt-2">
            <div className="h-full bg-gradient-to-r from-[#E65F2B] to-[#EAB308]" style={{ width: `${info.percent}%` }} />
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4 text-xs text-white/50">
            <div>
              <p className="text-white/30">Total size</p>
              <p className="font-display text-base font-bold text-white mt-0.5">{sizeMB ? `${sizeMB} MB` : "—"}</p>
            </div>
            <div>
              <p className="text-white/30">Remaining</p>
              <p className="font-display text-base font-bold text-white mt-0.5">{remainingMB ? `${remainingMB} MB` : "—"}</p>
            </div>
          </div>
        </div>
      )}

      <div className="card-surface p-6 mt-6">
        <h3 className="font-display text-lg font-bold mb-2">Pick the same video file</h3>
        <p className="text-sm text-white/50 mb-4">
          For your security, browsers require you to re-select the original file from disk.
          The upload will resume exactly where it left off — no re-uploading the bytes that already made it through.
        </p>
        <input
          type="file"
          accept="video/*"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm w-full"
          disabled={submitting}
          data-testid="resume-file-input"
        />
        {file && (
          <p className={`text-xs mt-3 ${info.total_size_bytes && file.size !== info.total_size_bytes ? "text-red-400" : "text-[#E65F2B]"}`}>
            {info.total_size_bytes && file.size !== info.total_size_bytes ? "✗" : "✓"} {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
            {info.total_size_bytes && file.size !== info.total_size_bytes && " (size doesn't match — please pick the original file)"}
          </p>
        )}
      </div>

      {submitting && (
        <div className="card-surface p-6 mt-6" data-testid="resume-progress">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-white/70">
              {phase === "uploading" && "Resuming upload to Cloudflare..."}
              {phase === "processing" && "Cloudflare is transcoding — almost done!"}
            </span>
            <span className="text-[#E65F2B] font-bold">{progress}%</span>
          </div>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#E65F2B] to-[#EAB308] transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-3">
        {!submitting && (
          <>
            <button
              onClick={startResume}
              disabled={!file || (info.total_size_bytes && file.size !== info.total_size_bytes)}
              className="ember-btn disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="resume-start-button"
            >
              <Upload className="h-4 w-4" /> Resume upload
            </button>
            <button
              onClick={deleteMovie}
              className="ghost-btn hover:border-red-500/50 hover:text-red-400"
              data-testid="resume-cancel-button"
            >
              <Trash2 className="h-4 w-4" /> Cancel & delete draft
            </button>
          </>
        )}
        {submitting && phase === "uploading" && (
          <button onClick={cancelResume} className="ghost-btn !py-2 !px-4 text-sm" data-testid="resume-pause-button">
            Pause
          </button>
        )}
      </div>
    </div>
  );
}
